/**
 * `/saved` — the saved-posts collection (SPEC `saved-posts` § `/saved` screen, endpoint #66).
 *
 * The `/feed` screen's chrome with a different body: the sticky pill header, the 288px sidebar and
 * the `?post=` preview are the feed's, and the card is `FeedCard` — a saved post is the same post,
 * so it has to look and behave like one. What is new is the **grid**: as many 552px columns as fit,
 * balanced by measured height rather than by CSS columns, so the server's order still reads
 * left-to-right and an infinite-scroll append never re-places a card someone is reading.
 *
 * Three things worth knowing about this file:
 *
 *  - **The ui-state gate.** `saved:main` loads before the first #66 request (§12.4), so a persisted
 *    selection is never briefly ignored and page one is never fetched twice.
 *  - **The range defaults to all time**, deliberately (§5.2 deviation): a collection shows itself
 *    until you narrow it. The sidebar's Saved-date section bounds a different column entirely.
 *  - **Search is session-only.** It is not in the stored blob, and it is debounced into the query
 *    key rather than firing per keystroke.
 *
 * The annotation rows under each card header are not this screen's: they ride `FeedCardDTO.savedInfo`
 * and render on every card surface (`FeedCard`), so a saved post looks the same wherever it appears.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link } from 'react-router';
import type { SavedCardDTO, SavedFiltersState, ViewRangeState } from '@lw/shared';
import { pageTitle } from '../../app/appTitle';
import { FEED_PATH } from '../../app/routes';
import { CloseIcon, EmptyState, ErrorState, Spinner, cx } from '../../components';
import { useContainerWidth } from '../../components/chart/useContainerWidth';
import { useDebouncedCallback, useDismissable, useDocumentTitle, useInView, useMediaQuery } from '../../lib/hooks';
import { useGroupsQuery, useProfilesQuery } from '../home/useHomeData';
import { NotionSaveProvider } from '../notion/useNotionSave';
import { SettingsModal } from '../settings/SettingsModal';
import {
  useAppTimezone,
  useMediaEnabled,
  useSettingsQuery,
  useTimezoneBootstrap,
} from '../settings/useSettings';
import { FeedCard } from '../view/feed/FeedCard';
import { PostPreviewProvider, usePostPreview } from '../view/PreviewContext';
import { anchorNow, resolveRange, type ViewSort } from '../view/viewState';
import { SavedHeader } from './SavedHeader';
import { SavedSidebar } from './SavedSidebar';
import {
  DEFAULT_OPEN_SAVED_SECTIONS,
  pruneSelection,
  toSavedParams,
  totalFilterCount,
  type SavedSectionId,
} from './savedState';
import { useBalancedColumns } from './useBalancedColumns';
import { useSavedListsQuery, useSavedTagsQuery } from './useSavedCollections';
import { useSavedQuery } from './useSavedQuery';
import { useSavedUiState } from './useSavedUiState';
import '../feed/feed.css';
import './saved.css';

/** Below this the sidebar unmounts and the header grows a Filter trigger (as on `/feed`). */
const SIDEBAR_QUERY = '(min-width: 1100px)';
/** One card track, and the gutter between two of them — the feed's 552px column, side by side. */
const CARD_WIDTH = 552;
const COLUMN_GAP = 24;
const SEARCH_DEBOUNCE_MS = 300;

export function SavedPage() {
  useTimezoneBootstrap();
  useDocumentTitle(pageTitle('Saved Posts'));

  const timezone = useAppTimezone();
  const mediaEnabled = useMediaEnabled();
  const countedPostTypes = useSettingsQuery().data?.countedPostTypes ?? [];
  const [settingsOpen, setSettingsOpen] = useState(false);

  const uiState = useSavedUiState();
  const wide = useMediaQuery(SIDEBAR_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSections, setOpenSections] = useState<ReadonlySet<SavedSectionId>>(
    () => new Set(DEFAULT_OPEN_SAVED_SECTIONS),
  );

  const listsQuery = useSavedListsQuery();
  const tagsQuery = useSavedTagsQuery();
  const lists = listsQuery.data?.lists ?? [];
  const tags = tagsQuery.data?.tags ?? [];

  // Unlike `/feed`, archived profiles are **not** filtered out: a save outlives archiving (§6.6),
  // so the author of an archived profile's saved post must stay selectable.
  const profilesQuery = useProfilesQuery();
  const groupsQuery = useGroupsQuery();
  const groups = groupsQuery.data?.groups ?? [];
  const profiles = profilesQuery.data?.profiles ?? [];

  /* ------------------------------------------------------------ search */

  // Two states on purpose: the field stays responsive while the query key only moves every 300 ms.
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const commitSearch = useDebouncedCallback(
    (next: string) => setCommittedSearch(next),
    SEARCH_DEBOUNCE_MS,
  );

  /* ----------------------------------------------------------- range/params */

  const rangeKey = `${uiState.state.range.preset}|${uiState.state.range.from ?? ''}|${uiState.state.range.to ?? ''}`;
  const now = useMemo(() => anchorNow(), [rangeKey]);
  const resolvedRange = useMemo(() => resolveRange(uiState.state.range, now), [rangeKey, now]);

  const filtersKey = JSON.stringify(uiState.state.filters);
  const sortKey = `${uiState.state.sort.key}|${uiState.state.sort.dir}`;
  const params = useMemo(
    () => toSavedParams(uiState.state, resolvedRange, committedSearch),
    [filtersKey, sortKey, resolvedRange, committedSearch],
  );

  const saved = useSavedQuery(params, uiState.ready);
  const cards = saved.data?.pages.flatMap((page) => page.cards) ?? [];
  // `meta` rides the cursor-less page only, which is always the first one in the cache.
  const meta = saved.data?.pages[0]?.meta;

  /* ------------------------------------------------------------- pruning */

  // A persisted selection can outlive the list or tag it names. The server ignores dangling ids
  // regardless; this keeps the *sidebar* honest. Runs once per load, and only when something dangles.
  const prunedRef = useRef(false);
  useEffect(() => {
    if (prunedRef.current || !uiState.ready) return;
    if (!listsQuery.isSuccess || !tagsQuery.isSuccess) return;
    if (!groupsQuery.isSuccess || !profilesQuery.isSuccess) return;
    prunedRef.current = true;
    const next = pruneSelection(uiState.state.filters, {
      lists: new Set(lists.map((list) => list.id)),
      tags: new Set(tags.map((tag) => tag.id)),
      groups: new Set(groups.map((group) => group.id)),
      // Every profile, archived included: a deletion prunes the selection, archiving does not.
      profiles: new Set(profiles.map((profile) => profile.id)),
    });
    if (next !== null) uiState.update({ filters: next });
  }, [
    uiState,
    listsQuery.isSuccess,
    tagsQuery.isSuccess,
    groupsQuery.isSuccess,
    profilesQuery.isSuccess,
    lists,
    tags,
    groups,
    profiles,
  ]);

  /* ---------------------------------------------------------------- render */

  const setFilters = (filters: SavedFiltersState) => uiState.update({ filters });
  const setRange = (range: ViewRangeState) => uiState.update({ range });
  const setSort = (sort: ViewSort) => uiState.update({ sort });
  const toggleSection = (section: SavedSectionId) =>
    setOpenSections((previous) => {
      const next = new Set(previous);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });

  const filterCount = totalFilterCount(uiState.state.filters);
  const narrowed = filterCount > 0 || committedSearch.trim().length > 0;

  const sidebar = (
    <SavedSidebar
      filters={uiState.state.filters}
      onChange={setFilters}
      lists={lists}
      tags={tags}
      groups={groups}
      profiles={profiles}
      mediaEnabled={mediaEnabled}
      countedPostTypes={countedPostTypes}
      openSections={openSections}
      onToggleSection={toggleSection}
    />
  );

  return (
    <NotionSaveProvider onOpenSettings={() => setSettingsOpen(true)}>
      <PostPreviewProvider mediaEnabled={mediaEnabled} timezone={timezone}>
        <div className="feed-screen">
          <SavedHeader
            summaryDisplay={meta?.summaryDisplay}
            search={search}
            onSearchChange={(next) => {
              setSearch(next);
              commitSearch(next);
            }}
            sort={uiState.state.sort}
            onSortChange={setSort}
            range={uiState.state.range}
            resolvedRange={resolvedRange}
            onRangeChange={setRange}
            timezone={timezone}
            now={now}
            onOpenFilters={wide ? null : () => setDrawerOpen(true)}
            filterCount={filterCount}
          />

          <div className="feed-screen__body">
            <div className={cx('saved-layout', !wide && 'saved-layout--narrow')}>
              {wide ? <aside className="saved-layout__side">{sidebar}</aside> : null}

              <main className="saved-layout__main">
                {uiState.error !== null ? (
                  <ErrorState
                    title="Couldn't restore your saved filters"
                    description="The collection is showing everything. Retrying re-reads what was stored."
                    onRetry={uiState.retry}
                  />
                ) : null}

                <SavedGrid
                  cards={cards}
                  mediaEnabled={mediaEnabled}
                  isPending={!uiState.ready || saved.isPending}
                  error={saved.isError ? saved.error : null}
                  onRetry={() => void saved.refetch()}
                  retrying={saved.isFetching}
                  hasNextPage={saved.hasNextPage}
                  isFetchingNextPage={saved.isFetchingNextPage}
                  fetchNextPage={() => void saved.fetchNextPage()}
                  empty={
                    narrowed ? (
                      <EmptyState
                        title="No saved posts match"
                        description="Clear the filters, widen the date range, or search for something else."
                        action={
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={() => {
                              setFilters({});
                              setSearch('');
                              setCommittedSearch('');
                            }}
                          >
                            Clear
                          </button>
                        }
                      />
                    ) : (
                      <EmptyState
                        title="Nothing saved yet"
                        description="Save posts from the feed — the bookmark button on any card puts it here."
                        action={
                          <Link className="btn-primary" to={FEED_PATH}>
                            Go to the feed
                          </Link>
                        }
                      />
                    )
                  }
                />
              </main>
            </div>
          </div>

          {!wide && drawerOpen ? (
            <SavedDrawer onClose={() => setDrawerOpen(false)}>{sidebar}</SavedDrawer>
          ) : null}

          <SettingsModal
            open={settingsOpen}
            initialTab="notion"
            onClose={() => setSettingsOpen(false)}
          />
        </div>
      </PostPreviewProvider>
    </NotionSaveProvider>
  );
}

/* -------------------------------------------------------------------- grid */

type SavedGridProps = {
  cards: SavedCardDTO[];
  mediaEnabled: boolean;
  isPending: boolean;
  error: Error | null;
  onRetry: () => void;
  retrying: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  empty: ReactNode;
};

function SavedGrid({
  cards,
  mediaEnabled,
  isPending,
  error,
  onRetry,
  retrying,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  empty,
}: SavedGridProps) {
  const { openPreview } = usePostPreview();
  const [wrapperRef, width] = useContainerWidth<HTMLDivElement>();
  const ids = cards.map((card) => card.id);

  // How many whole 552px tracks fit, counting the gutters between them. One below the two-column
  // breakpoint, which is exactly the feed's single track.
  const columnCount = Math.max(1, Math.floor((width + COLUMN_GAP) / (CARD_WIDTH + COLUMN_GAP)));
  const { columns, measureRef } = useBalancedColumns(cards, columnCount, (card) => card.id);

  const sentinelRef = useInView<HTMLDivElement>(hasNextPage && !isFetchingNextPage, fetchNextPage, {
    rootMargin: '400px',
  });

  return (
    <div className="saved-grid" ref={wrapperRef}>
      {isPending ? <Spinner size="lg" label="Loading saved posts…" /> : null}

      {error !== null ? (
        <ErrorState
          title="Couldn't load your saved posts"
          error={error}
          onRetry={onRetry}
          retrying={retrying}
        />
      ) : null}

      {!isPending && error === null && cards.length === 0 ? empty : null}

      <div
        className="saved-grid__columns"
        style={{ '--saved-columns': columnCount } as CSSProperties}
      >
        {columns.map((column, index) => (
          <div className="saved-grid__column" key={index}>
            {column.map((card) => (
              <div
                className="saved-grid__cell"
                key={card.id}
                ref={(node) => measureRef(card.id, node)}
              >
                <FeedCard
                  card={card}
                  mediaEnabled={mediaEnabled}
                  onOpen={(postId) => openPreview(postId, ids)}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      <div ref={sentinelRef} className="feed__sentinel">
        {isFetchingNextPage ? <Spinner label="Loading more…" /> : null}
        {!hasNextPage && cards.length > 0 ? (
          <span className="muted">That is everything you have saved.</span>
        ) : null}
      </div>
    </div>
  );
}

/** The sidebar as a left slide-in under the breakpoint — the feed's drawer, verbatim. */
function SavedDrawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useDismissable<HTMLDivElement>(true, onClose);

  return (
    <div className="feed-drawer" role="dialog" aria-label="Filters" aria-modal="true">
      <div className="feed-drawer__scrim" />
      <div className="feed-drawer__panel" ref={ref}>
        <div className="feed-drawer__head">
          <span className="feed-drawer__title">Filters</span>
          <button type="button" className="icon-btn" aria-label="Close filters" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default SavedPage;
